import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import type { Contact } from "./RFQ.ts";

export type List = {
    id: number;
    name: string;
    description: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    itemType: "CONTACT";
    teamId: number;
    createdByUserId: number;
    _count: {
        listContacts: number;
    };
};

export default {
    getContactLists: async ({
        searchString = "",
        page = 1,
        limit = 10,
        sortBy = "name",
        sortOrder = "asc",
    }: {
        searchString?: string;
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: "asc" | "desc";
    }): Promise<{
        data: List[];
        pagination: {
            page: number;
            limit: number;
            total: number;
            totalUnfiltered: number;
            totalUnnamedCount: number;
            totalPages: number;
        };
    }> => {
        const response = await fetch(
            `${PURCHASER_API_BASE_URL}/lists/contacts?searchString=${encodeURIComponent(searchString)}&page=${page}&limit=${limit}&sortBy=${sortBy}&sortOrder=${sortOrder}`,
            {
                method: "GET",
                headers: getHeaders(),
            },
        );
        if (!response.ok) {
            throw new Error("Failed to fetch contact lists");
        }
        return response.json();
    },

    getContactListById: async (listId: number): Promise<List> => {
        const response = await fetch(`${PURCHASER_API_BASE_URL}/lists/contacts/${listId}`, {
            method: "GET",
            headers: getHeaders(),
        });
        if (!response.ok) {
            throw new Error("Failed to fetch contact list");
        }
        return response.json();
    },

    getContactListContactsByListId: async (listId: number): Promise<Contact[]> => {
        const response = await fetch(`${PURCHASER_API_BASE_URL}/lists/contacts/${listId}/contacts`, {
            method: "GET",
            headers: getHeaders(),
        });
        if (!response.ok) {
            throw new Error("Failed to fetch contact list contacts");
        }
        return response.json();
    },

    createContactList: async (name: string, description?: string, contacts?: number[]) => {
        const response = await fetch(`${PURCHASER_API_BASE_URL}/lists/contacts`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ name, description, contacts }),
        });
        if (!response.ok) {
            throw new Error("Failed to create contact list");
        }
        return response.json();
    },

    addContactsToContactList: async (listId: number, contacts: number[]) => {
        const response = await fetch(`${PURCHASER_API_BASE_URL}/lists/contacts/${listId}/contacts`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ contacts }),
        });
        if (!response.ok) {
            throw new Error("Failed to add contacts to list");
        }
        return response.json();
    },

    removeContactsFromContactList: async (listId: number, contacts: number[]) => {
        const response = await fetch(`${PURCHASER_API_BASE_URL}/lists/contacts/${listId}/contacts`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({ contacts }),
        });
        if (!response.ok) {
            throw new Error("Failed to remove contacts from list");
        }
        return response.json();
    },

    archiveList: async (listId: number) => {
        const response = await fetch(`${PURCHASER_API_BASE_URL}/lists/contacts/${listId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        });
        if (!response.ok) {
            throw new Error("Failed to archive list");
        }
        return response.json();
    },

    duplicateList: async (listId: number, newName: string, newDescription: string) => {
        const response = await fetch(`${PURCHASER_API_BASE_URL}/lists/contacts/${listId}/duplicate`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify({ name: newName, description: newDescription }),
        });
        if (!response.ok) {
            throw new Error("Failed to duplicate list");
        }
        return response.json();
    },

    editContactList: async (listId: number, newName: string, newDescription: string) => {
        const response = await fetch(`${PURCHASER_API_BASE_URL}/lists/contacts/${listId}`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify({ name: newName, description: newDescription }),
        });
        if (!response.ok) {
            // Indicates an list with the same name already exists
            if (response.status === 400) {
                throw new Error("A contact list with this name already exists", { cause: "DUPLICATE_NAME" });
            }
            throw new Error("Failed to edit contact list");
        }
        return response.json();
    },
};
